import { hasWorkflowDraftChanges } from 'nocodb-sdk'
import type { WorkflowNodeDefinition, WorkflowType } from 'nocodb-sdk'
import { DlgWorkflowCreate } from '#components'

export const useWorkflowStore = defineStore('workflow', () => {
  const { $api, $e, $poller } = useNuxtApp()

  const { isUIAllowed } = useRoles()

  const { ncNavigateTo } = useGlobal()

  const { t } = useI18n()

  const { refreshCommandPalette } = useCommandPalette()

  const { isFeatureEnabled } = useBetaFeatureToggle()

  const { blockWorkflows, showUpgradeToUseWorkflows } = useEeConfig()

  const router = useRouter()

  const route = useRoute()

  const baseStore = useBases()

  const { loadProject } = baseStore

  const { activeProjectId, bases, basesUser } = storeToRefs(baseStore)

  const { activeWorkspaceId, isSharedBase } = storeToRefs(useWorkspace())

  const isAdvancedNodesEnabled = computed(() => isFeatureEnabled(FEATURE_FLAG.ADVANCED_NODES))

  const isWorkflowEditAllowed = computed(() => isUIAllowed('workflowCreateOrEdit'))

  const baseUsers = computed(() => (activeProjectId.value ? basesUser.value.get(activeProjectId.value) || [] : []))

  // State
  const workflows = ref<Map<string, (WorkflowType & { _dirty?: string | number; ___is_new?: boolean })[]>>(new Map())

  const workflowNodes = ref<Map<string, WorkflowNodeDefinition[]>>(new Map())

  const isLoadingWorkflow = ref(false)

  const activeBaseWorkflows = computed(() => {
    if (!activeProjectId.value) return []

    return (workflows.value.get(activeProjectId.value) || []).map((wf) => {
      const createdUser = baseUsers.value.find((u) => u.id === wf.created_by)
      const updatedUser = wf.updated_by ? baseUsers.value.find((u) => u.id === wf.updated_by) : null

      return {
        ...wf,
        created_by_user: createdUser || null,
        updated_by_user: updatedUser || null,
      }
    })
  })

  const activeBaseNodeSchemas = computed<Array<WorkflowNodeDefinition>>(() => {
    if (!activeProjectId.value) return []
    return (workflowNodes.value.get(activeProjectId.value) || []).filter((node) =>
      node.hidden ? isAdvancedNodesEnabled.value : true,
    )
  })

  const activeWorkflowId = computed(() => route.params.workflowId as string)

  const activeWorkflow = computed(() => {
    if (!activeWorkflowId.value) return null
    return activeBaseWorkflows.value.find((a) => a.id === activeWorkflowId.value) || null
  })

  const activeWorkflowUrlSlug = computed(() => {
    return route.params.slugs?.[0] || ''
  })

  const activeWorkflowReadableUrlSlug = computed(() => {
    if (!activeWorkflow.value) return ''

    return toReadableUrlSlug([activeWorkflow.value.title])
  })

  const activeWorkflowHasDraftChanges = computed(() => {
    if (!activeWorkflow.value) return false
    if (!isWorkflowEditAllowed.value) return false
    return hasWorkflowDraftChanges(activeWorkflow.value)
  })

  // Actions
  const loadWorkflows = async ({ baseId, force = false }: { baseId: string; force?: boolean }) => {
    if (!activeWorkspaceId.value || isSharedBase.value) return []

    if (workflows.value.has(baseId) && !force) {
      return workflows.value.get(baseId) || []
    }

    try {
      isLoadingWorkflow.value = true

      const response = (await $api.internal.getOperation(activeWorkspaceId.value, baseId, {
        operation: 'workflowList',
      })) as WorkflowType[]

      if (ncIsArray(response)) {
        workflows.value.set(baseId, response)
        return response
      } else {
        return []
      }
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return []
    } finally {
      isLoadingWorkflow.value = false
    }
  }

  const loadWorkflow = async (workflowId: string, showLoader = true) => {
    if (!activeProjectId.value || !activeWorkspaceId.value || !workflowId) {
      return null
    }

    let workflow: null | WorkflowType = null

    if (workflows.value.get(activeProjectId.value)?.find((a) => a.id === workflowId)) {
      workflow = (workflows.value.get(activeProjectId.value) ?? []).find((a) => a.id === workflowId) || null
    }

    try {
      if (showLoader) {
        isLoadingWorkflow.value = true
      }

      workflow =
        workflow ||
        ((await $api.internal.getOperation(activeWorkspaceId.value, activeProjectId.value, {
          operation: 'workflowGet',
          workflowId,
        })) as unknown as WorkflowType)

      const baseWorkflows = workflows.value.get(activeProjectId.value) || []
      const filtered = baseWorkflows.filter((a) => a.id !== workflowId)
      filtered.push(workflow)
      filtered.sort((a, b) => (a?.order || 0) - (b?.order || 0))
      workflows.value.set(activeProjectId.value, filtered)

      return workflow
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      ncNavigateTo({
        workspaceId: activeWorkspaceId.value,
        baseId: activeProjectId.value,
      })
      return null
    } finally {
      if (showLoader) {
        isLoadingWorkflow.value = false
      }
    }
  }

  const createWorkflow = async (baseId: string, workflowData: Partial<WorkflowType>) => {
    if (!activeWorkspaceId.value) return null
    try {
      const created = await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'workflowCreate',
        },
        workflowData,
      )

      const baseWorkflows = workflows.value.get(baseId) || []
      baseWorkflows.push({
        ...created,
        ___is_new: true,
      })

      workflows.value.set(baseId, baseWorkflows)

      ncNavigateTo({
        workspaceId: activeWorkspaceId.value,
        baseId: activeProjectId.value,
        workflowId: created.id,
        workflowTitle: created.title,
      })

      await refreshCommandPalette()

      return created
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return null
    }
  }

  const updateWorkflow = async (
    baseId: string,
    workflowId: string,
    updates: Partial<WorkflowType>,
    options?: {
      bumpDirty?: boolean
      skipNetworkCall?: boolean
    },
  ) => {
    if (!activeWorkspaceId.value) return null
    try {
      const workflow = workflows.value.get(baseId)?.find((a) => a.id === workflowId)
      const updated = options?.skipNetworkCall
        ? {
            ...workflow,
            ...updates,
          }
        : await $api.internal.postOperation(
            activeWorkspaceId.value,
            baseId,
            {
              operation: 'workflowUpdate',
              workflowId,
            },
            (() => {
              const body: Record<string, unknown> = {}
              if ('title' in updates) body.title = updates.title
              if ('description' in updates) body.description = updates.description
              if ('meta' in updates) body.meta = updates.meta
              if ('order' in updates) body.order = updates.order
              if ('type' in updates) body.type = updates.type
              if ('enabled' in updates) body.enabled = updates.enabled
              if ('nodes' in updates) body.nodes = updates.nodes
              if ('edges' in updates) body.edges = updates.edges
              if ('draft' in updates) body.draft = updates.draft
              return body
            })(),
          )

      if (options?.bumpDirty) {
        updated._dirty = workflow?._dirty ? +workflow?._dirty + 1 : 1
      }

      const baseWorkflows = workflows.value.get(baseId) || []
      const index = baseWorkflows.findIndex((a) => a.id === workflowId)

      if (index !== -1) {
        const updatedWorkflows = [...baseWorkflows]
        updatedWorkflows[index] = {
          ...baseWorkflows[index],
          ...updated,
        } as unknown as WorkflowType
        workflows.value.set(baseId, updatedWorkflows)
      }

      await refreshCommandPalette()

      if (!options?.skipNetworkCall) {
        $e('a:workflow:update')
      }

      return updated
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return null
    }
  }

  const deleteWorkflow = async (baseId: string, workflowId: string) => {
    if (!activeWorkspaceId.value) return null
    try {
      await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'workflowDelete',
        },
        {
          workflowId,
        },
      )

      await refreshCommandPalette()

      $e('a:workflow:delete')

      // Update local state
      const baseWorkflows = workflows.value.get(baseId) || []
      const filtered = baseWorkflows.filter((a) => a.id !== workflowId)
      workflows.value.set(baseId, filtered)

      if (activeWorkflowId.value === workflowId) {
        const nextWorkflow = filtered[0]
        if (nextWorkflow) {
          ncNavigateTo({
            workspaceId: activeWorkspaceId.value,
            baseId: activeProjectId.value,
            workflowId: nextWorkflow.id,
            workflowTitle: nextWorkflow.title,
          })
        }
      }

      if (!filtered.length) {
        ncNavigateTo({
          workspaceId: activeWorkspaceId.value,
          baseId: activeProjectId.value,
        })
      }

      return true
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return false
    }
  }

  const duplicateWorkflow = async (baseId: string, workflowId: string, navigate = true) => {
    if (!activeWorkspaceId.value) return null

    try {
      const created = await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'workflowDuplicate',
        },
        {
          workflowId,
        },
      )

      const baseWorkflows = workflows.value.get(baseId) || []
      baseWorkflows.push(created)
      workflows.value.set(baseId, baseWorkflows)

      if (navigate) {
        ncNavigateTo({
          workspaceId: activeWorkspaceId.value,
          baseId: activeProjectId.value,
          workflowId: created.id,
          workflowTitle: created.title,
        })
      } else {
        message.toast(t('msg.success.workflowDuplicated'))
      }

      await refreshCommandPalette()

      $e('a:workflow:duplicate')

      return created
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return null
    }
  }

  const publishWorkflow = async (workflowId: string, params?: { cancelPendingExecutions?: boolean }) => {
    if (!activeWorkspaceId.value || !activeProjectId.value) return null
    try {
      const published = await $api.internal.postOperation(
        activeWorkspaceId.value,
        activeProjectId.value,
        {
          operation: 'workflowPublish',
        },
        {
          workflowId,
          ...params,
        },
      )

      const baseWorkflows = workflows.value.get(activeProjectId.value) || []
      const index = baseWorkflows.findIndex((a) => a.id === workflowId)

      if (index !== -1) {
        const updatedWorkflows = [...baseWorkflows]
        updatedWorkflows[index] = {
          ...published,
          _dirty: published._dirty ? published._dirty + 1 : 1,
        } as WorkflowType
        workflows.value.set(activeProjectId.value, updatedWorkflows)
      }

      $e('a:workflow:publish')
      return published
    } catch (e) {
      console.error(e)
      const errorInfo = await extractSdkResponseErrorMsgv2(e as any)
      message.error(errorInfo?.message)
    }
  }

  const loadWorkflowNodes = async () => {
    if (!activeWorkspaceId.value || !activeProjectId.value || isSharedBase.value) return
    if (blockWorkflows.value) return

    try {
      const response = await $api.internal.getOperation(activeWorkspaceId.value, activeProjectId.value, {
        operation: 'workflowNodes',
      })

      const nodes = ncIsArray(response?.nodes) ? response.nodes : []

      workflowNodes.value.set(activeProjectId.value, nodes as any)
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
    }
  }

  const loadWorkflowExecutions = async (params: { workflowId?: string; limit?: number; offset?: number; cursorId?: string }) => {
    if (!activeWorkspaceId.value || !activeProjectId.value) return []
    if (!isUIAllowed('workflowExecutionList')) return []
    try {
      const response = await $api.internal.getOperation(activeWorkspaceId.value, activeProjectId.value, {
        operation: 'workflowExecutionList',
        workflowId: params.workflowId,
        limit: params.limit,
        offset: params.offset,
        cursorId: params.cursorId,
      })

      return ncIsArray(response) ? response : []
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return []
    }
  }

  const getWorkflowExecution = async (params: { workflowId: string; executionId: string }) => {
    if (!activeWorkspaceId.value || !activeProjectId.value) return null
    if (!isUIAllowed('workflowExecutionList')) return null
    try {
      const response = await $api.internal.getOperation(activeWorkspaceId.value, activeProjectId.value, {
        operation: 'workflowExecutionGet',
        workflowId: params.workflowId,
        executionId: params.executionId,
      })

      return response || null
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const executeWorkflow = async (workflowId: string) => {
    if (!activeWorkspaceId.value || !activeProjectId.value) return
    try {
      const jobData = await $api.internal.postOperation(
        activeWorkspaceId.value,
        activeProjectId.value,
        {
          operation: 'workflowExecute',
        },
        {
          workflowId,
          triggerData: {
            timestamp: Date.now(),
          },
        },
      )

      await new Promise<void>((resolve, reject) => {
        $poller.subscribe(
          { id: jobData.id },
          (data: {
            id: string
            status?: string
            data?: {
              error?: { message: string }
              message?: string
              result?: any
            }
          }) => {
            if (data.status !== 'close') {
              if (data.status === JobStatus.COMPLETED) {
                const executionState = data.data?.result
                if (!executionState) {
                  resolve()
                  return
                }
                if (executionState.status === 'completed' || executionState.status === 'success') {
                  const duration = ((executionState.endTime - executionState.startTime) / 1000).toFixed(2)
                  const nodesCount = executionState?.nodeResults?.length
                  message.success(`Workflow executed successfully in ${duration}s (${nodesCount} nodes executed)`)
                  resolve()
                } else if (executionState.status === 'error') {
                  const errorNode = executionState?.nodeResults?.find((r: any) => r.status === 'error')
                  const errorMessage = errorNode
                    ? `Node "${errorNode.nodeTitle}" failed: ${errorNode.error}`
                    : 'Workflow execution failed'
                  message.error(errorMessage)
                  console.error('[Workflow] Execution error:', executionState)
                  reject(new Error(errorMessage))
                } else if (executionState.status === 'waiting') {
                  message.info('Workflow paused - waiting for delay')
                  resolve()
                } else if (executionState.status === 'skipped') {
                  message.info('Workflow execution skipped')
                  resolve()
                } else {
                  // Handle other completion states
                  resolve()
                }
              } else if (data.status === JobStatus.FAILED) {
                const errorMsg = data.data?.error?.message || 'Workflow execution failed'
                message.error(errorMsg)
                reject(new Error(errorMsg))
              }
            }
          },
        )
      })
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
    }
  }

  const openWorkflow = async (workflow: WorkflowType) => {
    if (!workflow.base_id || !workflow.id) return

    let base = bases.value.get(workflow.base_id)
    if (!base) {
      await loadProject(workflow.base_id)
      base = bases.value.get(workflow.base_id)
      if (!base) throw new Error('Base not found')
    }

    let workspaceIdOrType = activeWorkspaceId.value
    if (['nc', 'base'].includes(route.params.typeOrId as string)) {
      workspaceIdOrType = route.params.typeOrId as string
    }

    let baseIdOrBaseId = base.id
    if (['base'].includes(route.params.typeOrId as string)) {
      baseIdOrBaseId = route.params.baseId as string
    }

    $e('c:workflow:open')

    ncNavigateTo({
      workspaceId: workspaceIdOrType,
      baseId: baseIdOrBaseId,
      workflowId: workflow.id,
      workflowTitle: workflow.title,
    })
  }

  async function openNewWorkflowModal({
    baseId,
    e,
    loadWorkflowsOnClose,
    scrollOnCreate,
  }: {
    baseId?: string
    e?: string
    loadWorkflowsOnClose?: boolean
    scrollOnCreate?: boolean
  }) {
    if (!baseId) return

    if (blockWorkflows.value) {
      showUpgradeToUseWorkflows()
      return
    }

    const isDlgOpen = ref(true)

    const { close } = useDialog(DlgWorkflowCreate, {
      'modelValue': isDlgOpen,
      'baseId': baseId,
      'onUpdate:modelValue': () => closeDialog(),
      'onCreated': async (workflow: WorkflowType) => {
        closeDialog()

        if (loadWorkflowsOnClose) {
          await loadWorkflows({ baseId, force: true })
        }

        $e(e ?? 'a:workflow:create')

        if (!workflow) return

        if (scrollOnCreate) {
          setTimeout(() => {
            const newWorkflowDom = document.querySelector(`[data-workflow-id="${workflow.id}"]`)
            if (!newWorkflowDom) return

            // Scroll to the workflow node
            newWorkflowDom?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }, 1000)
        }
      },
    })

    function closeDialog() {
      isDlgOpen.value = false
      close(1000)
    }
  }

  // Watch for active workflow changes
  watch(activeWorkflowId, async (workflowId) => {
    if (!activeProjectId.value) return
    if (workflowId) {
      await loadWorkflow(workflowId)
    }
  })

  watch(
    activeProjectId,
    async () => {
      if (activeWorkspaceId.value && activeProjectId.value) {
        await loadWorkflowNodes()
      }
    },
    { immediate: true },
  )

  /**
   * Keeps the browser URL slug in sync with the workflow's readable slug.
   * Triggers only when:
   * - The current browser URL slug is missing, OR
   * - The browser URL slug does not match the workflow's readable slug.
   */
  watch(
    [activeWorkflowReadableUrlSlug, activeWorkflowUrlSlug],
    ([newActiveWorkflowReadableUrlSlug, newActiveWorkflowUrlSlug]) => {
      if (!newActiveWorkflowReadableUrlSlug || newActiveWorkflowUrlSlug === newActiveWorkflowReadableUrlSlug) return

      const slugs = (route.params.slugs as string[]) || []

      const newSlug = [newActiveWorkflowReadableUrlSlug]

      if (slugs.length > 1) {
        newSlug.push(...slugs.slice(1))
      }

      router.replace({
        name: 'index-typeOrId-baseId-index-workflows-workflowId-slugs',
        params: {
          ...route.params,
          slugs: newSlug,
        },
        query: route.query,
        force: true,
      })
    },
    {
      immediate: true,
      flush: 'post',
    },
  )

  return {
    // State
    workflows,
    activeWorkflow,
    isLoadingWorkflow,

    // Getters
    activeBaseWorkflows,
    activeWorkflowId,
    activeBaseNodeSchemas,
    activeWorkflowHasDraftChanges,

    // Actions
    loadWorkflows,
    loadWorkflow,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    openWorkflow,
    duplicateWorkflow,
    publishWorkflow,
    openNewWorkflowModal,
    executeWorkflow,
    // Execution Logs
    loadWorkflowExecutions,
    getWorkflowExecution,
  }
})

// Enable HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWorkflowStore, import.meta.hot))
}
